<script setup>
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AutomationPreviewAccordion from 'dashboard/routes/dashboard/settings/sops/builder/AutomationPreviewAccordion.vue';

defineProps({
  steps: { type: Array, required: true },
});

const { t } = useI18n();
const agentById = useMapGetter('agents/getAgentById');
</script>

<template>
  <div class="border-t border-n-strong pt-3">
    <p class="mb-2 text-sm font-medium text-n-slate-12">
      {{ t('SOPS.SIMULATOR.SUMMARY.TITLE') }}
    </p>
    <ol class="mb-0 flex list-none flex-col gap-2 text-sm">
      <li
        v-for="(step, index) in steps"
        :key="step.key"
        class="flex min-w-0 gap-2"
      >
        <span class="shrink-0 text-n-slate-10">{{ `${index + 1}.` }}</span>
        <div class="flex min-w-0 flex-1 flex-col gap-0.5">
          <span class="whitespace-pre-wrap break-words text-n-slate-12">
            {{ step.label }}
          </span>
          <span
            v-if="step.branchLabel"
            class="whitespace-pre-wrap break-words text-n-blue-11"
          >
            {{
              t('SOPS.SIMULATOR.SUMMARY.CHOSE', { branch: step.branchLabel })
            }}
          </span>
          <span
            v-for="item in step.checkedItems"
            :key="item"
            class="flex items-start gap-1.5 text-n-slate-11"
          >
            <span class="i-lucide-check mt-0.5 size-3.5 shrink-0" />
            <span class="min-w-0 whitespace-pre-wrap break-words line-through">
              {{ item }}
            </span>
          </span>
          <AutomationPreviewAccordion
            v-if="step.automation"
            :id="step.automation.id"
            :type="step.automation.type"
            :name="step.automation.name"
            :toggle-label="step.automationLabel"
            toggle-class="text-sm text-n-blue-11"
            :show-name="false"
          />
          <div
            v-if="step.automationContent"
            class="my-2 whitespace-pre-wrap break-words rounded bg-n-solid-3 p-2 text-sm text-n-slate-12"
          >
            {{ step.automationContent }}
          </div>
          <span
            v-if="step.referencedSopName"
            class="flex items-center gap-1.5 text-n-teal-11"
          >
            <span class="i-lucide-corner-down-right size-3.5 shrink-0" />
            {{
              t('SOPS.SIMULATOR.SUMMARY.REFERENCED', {
                name: step.referencedSopName,
              })
            }}
          </span>
          <span
            v-if="step.confirmed"
            class="flex items-center gap-1.5 text-xs text-n-teal-11"
          >
            <span class="i-lucide-circle-check size-3.5 shrink-0" />
            {{ t('SOPS.SIMULATOR.CONFIRMED') }}
          </span>
          <div
            v-for="feedback in step.feedbacks"
            :key="feedback.user_id"
            class="flex flex-col gap-1 text-n-slate-11"
          >
            <span class="flex items-start gap-1.5">
              <span class="i-lucide-message-square mt-0.5 size-3.5 shrink-0" />
              <span class="min-w-0 whitespace-pre-wrap break-words italic">
                {{ feedback.text }}
              </span>
            </span>
            <span class="flex items-center gap-1.5 text-xs">
              {{ t('SOPS.SIMULATOR.FEEDBACK_SUBMITTED_BY') }}
              <Avatar
                :name="feedback.name"
                :src="agentById(feedback.user_id).thumbnail"
                :size="16"
                rounded-full
              />
              <span class="font-medium text-n-slate-12">{{
                feedback.name
              }}</span>
            </span>
          </div>
        </div>
      </li>
    </ol>
  </div>
</template>
